"use strict";
/**
 * APITable <https://github.com/apitable/apitable>
 * Copyright (C) 2022 APITable Ltd. <https://apitable.com>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.setRecords = void 0;
const command_manager_1 = require("../../command_manager");
const config_1 = require("../../config");
const lodash_1 = require("lodash");
const model_1 = require("../../model");
const store_1 = require("../../exports/store");
const types_1 = require("../../types");
const field_types_1 = require("../../types/field_types");
const utils_1 = require("../../utils");
function collectMemberProperty(datasheetId, actions, context) {
    const { model: state, memberFieldMaintainer } = context;
    const fieldMap = store_1.Selectors.getFieldMap(state, datasheetId);
    const isAddFieldAction = actions.map(item => item.p[3]).some(fieldId => !fieldMap[fieldId]);
    if (isAddFieldAction) {
        return actions;
    }
    const memberFieldIds = [];
    const unitIdsMap = new Map();
    // Check if there is a modification to the member field in the current OP, if so,
    // Collect OI (written) data into operateRecordIds, and also collect related fieldIds into memberFieldIds
    actions.forEach(item => {
        const fieldId = item.p[3];
        const field = fieldMap[fieldId];
        if (field.type !== field_types_1.FieldType.Member) {
            return;
        }
        memberFieldIds.push(fieldId);
        // oi and od exist in op, if only od is to delete data, there is no need to collect
        if ('oi' in item) {
            const existValue = unitIdsMap.get(fieldId) || [];
            unitIdsMap.set(fieldId, [...new Set([...existValue, ...item['oi']])]);
        }
    });
    // Put the data collected according to fieldId into the property of the corresponding field
    memberFieldIds.forEach(fieldId => {
        const collectUnitIds = unitIdsMap.get(fieldId) || [];
        const field = fieldMap[fieldId];
        // The unitIds of members are dynamically calculated, which will cause the position of the kanban to change in the kanban. 
        // Therefore, in the data collection, the unitIds stored in the property and all the data of the collected cells are merged, 
        // and the duplicates are removed.
        // This can ensure that the order of the existing data in unitIds does not change, 
        // and then perform "intersection" processing with the result and the collected data, and take out the same part 
        // (i.e. that is, the data from the cell phone, but it is guaranteed the data order of the original unitIds)
        const unDuplicateArray = [...new Set([...field.property.unitIds, ...collectUnitIds])];
        const newProperty = unDuplicateArray.filter(item => item);
        memberFieldMaintainer.insert(fieldId, newProperty, datasheetId);
    });
    return actions;
}
exports.setRecords = {
    undoable: true,
    execute: (context, options) => {
        const { model: state, ldcMaintainer, fieldMapSnapshot } = context;
        const { data: _data, internalFix, alarm } = options;
        const datasheetId = options.datasheetId || store_1.Selectors.getActiveDatasheetId(state);
        const mirrorId = options.mirrorId;
        const snapshot = store_1.Selectors.getSnapshot(state, datasheetId);
        const fieldPermissionMap = store_1.Selectors.getFieldPermissionMap(state);
        if (!snapshot) {
            return null;
        }
        const data = _data.filter(item => {
            const fieldRole = store_1.Selectors.getFieldRoleByFieldId(fieldPermissionMap, item.fieldId);
            if (fieldRole && fieldRole !== config_1.ConfigConstant.Role.Editor) {
                return false;
            }
            return true;
        });
        if ((0, lodash_1.isEmpty)(data)) {
            return null;
        }
        const fieldMap = snapshot.meta.fieldMap;
        const actions = data.reduce((collected, recordOption) => {
            const { recordId, fieldId, field: fieldProp } = recordOption;
            const field = fieldMap[fieldId] || fieldProp;
            let value = recordOption.value;
            // field does not exist, the data does not take effect
            if (!field || !model_1.Field.bindContext(field, state).recordEditable(datasheetId, mirrorId) && !(internalFix === null || internalFix === void 0 ? void 0 : internalFix.anonymouFix)) {
                return collected;
            }
            // Number/currency/percentage fields need special processing, string to number, number of significant digits, etc.
            if (field.type === field_types_1.FieldType.Number || field.type === field_types_1.FieldType.Currency || field.type === field_types_1.FieldType.Percent) {
                if ((0, lodash_1.isString)(value)) {
                    value = (0, utils_1.str2number)(value);
                }
                else if ((0, lodash_1.isNumber)(value)) {
                    value = (0, utils_1.num2number)(value);
                }
                else {
                    value = null;
                }
            }
            // There will be some data problems on the line, and brotherFieldId will also exist in the case of self-table association, 
            // resulting in the existence of redundant actions
            if (field.type === field_types_1.FieldType.Link && field.property.brotherFieldId && field.property.foreignDatasheetId !== datasheetId) {
                /**
                 * Data consistency maintenance for associated field cells:
                 * Guarantee the interrelated consistency of the cell data in brotherField in two different datasheets that are related to each other.
                 * That is: when an associated record is added to the associated field cell of a datasheet.
                 * In the related datasheet sibling fields, a corresponding association relationship should be created.
                 * The same is true when deleting a record.
                 */
                const oldValue = store_1.Selectors.getCellValue(state, snapshot, recordId, fieldId);
                const linkedSnapshot = store_1.Selectors.getSnapshot(state, field.property.foreignDatasheetId);
                ldcMaintainer.insert(state, linkedSnapshot, recordId, field, value, oldValue);
            }
            // if val.value is an empty array then it will be null
            value = (0, model_1.handleEmptyCellValue)(value, model_1.Field.bindContext(field, state).basicValueType);
            fieldMapSnapshot[field.id] = field;
            const action = model_1.DatasheetActions.setRecord2Action(snapshot, { recordId, fieldId, value });
            action && collected.push(action);
            if (field.type === field_types_1.FieldType.DateTime) {
                const cacheAlarm = store_1.Selectors.getDateTimeCellAlarm(snapshot, recordId, field.id);
                /**
                 * remove alarm
                 * 1. Delete date cell data
                 * 2. The date cell has data to remove the alarm clock directly
                 */
                if ((value === null && cacheAlarm) || (Boolean(value) && cacheAlarm && !alarm)) {
                    const alarmActions = model_1.DatasheetActions.setDateTimeCellAlarm(snapshot, {
                        recordId,
                        fieldId,
                        alarm: null,
                    });
                    if (alarmActions) {
                        collected.push(...alarmActions);
                    }
                }
                else if (Boolean(value) && !cacheAlarm && Boolean(alarm)) { // new alarm
                    const newAlarmId = (0, utils_1.getNewId)(utils_1.IDPrefix.DateTimeAlarm);
                    const alarmActions = model_1.DatasheetActions.setDateTimeCellAlarm(snapshot, {
                        recordId,
                        fieldId,
                        alarm: Object.assign(Object.assign({}, alarm), { id: newAlarmId }),
                    });
                    if (alarmActions) {
                        collected.push(...alarmActions);
                    }
                }
                else if (Boolean(value) && !(0, lodash_1.isEqual)(cacheAlarm, alarm)) { // edit alarm
                    const alarmActions = model_1.DatasheetActions.setDateTimeCellAlarm(snapshot, {
                        recordId,
                        fieldId,
                        alarm: Object.assign(Object.assign({}, cacheAlarm), alarm),
                    });
                    if (alarmActions) {
                        collected.push(...alarmActions);
                    }
                }
            }
            return collected;
        }, []);
        return {
            result: command_manager_1.ExecuteResult.Success,
            resourceId: datasheetId,
            resourceType: types_1.ResourceType.Datasheet,
            actions: collectMemberProperty(datasheetId, actions, context),
            fieldMapSnapshot
        };
    },
};
/*

 declare module '../../command_manager/command_manager' {
 interface CollaCommandManager {
 execute(options: ISetRecordsOptions & { cmd: 'SetRecords' });
 }
 }

 */
//# sourceMappingURL=set_records.js.map